import { QueryClient, useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  CreateReportPdfError,
  CreateReportPdfRequest,
  CreateReportPdfResponse,
  PostReportPdfPreviewDataError,
  PostReportPdfPreviewDataRequest,
  PostReportPdfPreviewDataResponse,
  ReportPdfPreviewError,
  ReportPdfPreviewRequest,
  ReportPdfPreviewResponse,
} from '@/api/model';

const KEY = {
  GET_REPORT_PDF_PREVIEW: 'GET_REPORT_PDF_PREVIEW',
} as const;

const request = {
  getReportPdfPreviewData: async (
    { payload: { studyId } }: ReportPdfPreviewRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<ReportPdfPreviewResponse>(
      `${ENDPOINTS.FCT.REPORT_PDF.STUDY}${studyId}/preview-data`,
      { signal },
    );

    return data;
  },
  postCreateReportPdf: async ({ payload }: CreateReportPdfRequest) => {
    const { data } = await fetchClient.FCT.post<CreateReportPdfResponse>(
      ENDPOINTS.FCT.REPORT_PDF.BASE,
      { body: payload },
    );

    return data;
  },
  postReportPdfPreviewData: async ({ payload, studyId }: PostReportPdfPreviewDataRequest) => {
    const { data } = await fetchClient.FCT.post<PostReportPdfPreviewDataResponse>(
      `${ENDPOINTS.FCT.REPORT_PDF.STUDY}${studyId}/preview-data`,
      { body: payload },
    );

    return data;
  },
};

const query = {
  useGetReportPdfPreviewData: (
    { payload }: ReportPdfPreviewRequest,
    config?: Partial<UseQueryOptions<ReportPdfPreviewResponse, ReportPdfPreviewError>>,
  ) =>
    useQuery<ReportPdfPreviewResponse, ReportPdfPreviewError>({
      queryFn: ({ signal }) => request.getReportPdfPreviewData({ payload }, signal),
      queryKey: [KEY.GET_REPORT_PDF_PREVIEW, payload.studyId],
      ...config,
    }),
};

const mutation = {
  usePostCreateReportPdf: () =>
    useMutation<CreateReportPdfResponse, CreateReportPdfError, CreateReportPdfRequest>({
      mutationFn: request.postCreateReportPdf,
    }),
  usePostReportPdfPreviewData: (queryClient: QueryClient) =>
    useMutation<
      PostReportPdfPreviewDataResponse,
      PostReportPdfPreviewDataError,
      PostReportPdfPreviewDataRequest
    >({
      mutationFn: request.postReportPdfPreviewData,
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: [KEY.GET_REPORT_PDF_PREVIEW, variables.studyId],
        });
      },
    }),
};

export const reportPdf = {
  KEY,
  mutation,
  query,
  request,
};
